import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { nanoid } from 'nanoid';
import { useMemo, useRef, useState } from 'react';

import { DatasheetAgentWorkbench } from '#components/datasheet-agent-workbench/datasheet-agent-workbench.js';
import { DATASHEET_POPOVER_WORKBENCH_STORAGE_KEYS } from '#components/datasheet-popover/datasheet-popover-workbench-storage.js';
import { usePortalDocument } from '#components/datasheet-popover/portal-document-context.js';
import { useDatasheetPopoverData } from '#components/datasheet-popover/use-datasheet-popover-data.js';
import { ConfidentialDatasheetBadge } from '#components/datasheet-viewer/confidential-datasheet-badge.js';
import {
	replaceDatasheetDialogBgProps,
	ReplaceDatasheetReason,
} from '#components/datasheet-viewer/replace-datasheet-dialog-utils.js';
import { ReplaceDatasheetDialog } from '#components/datasheet-viewer/replace-datasheet-dialog.js';
import { Loading } from '#components/loading.js';
import { SignalReferenceContextProvider } from '#components/markdown/plugins/signal-reference-context-provider.js';
import { ResizablePanel } from '#components/resizable-panel/resizable-panel.js';
import { SidePanelTab } from '#components/side-panel-tabs/side-panel-tab.js';
import { SidePanelTabs } from '#components/side-panel-tabs/side-panel-tabs.js';
import { DatasheetWindowEvidence } from '#package-store/package-family-editor/package-evidence-panel/datasheet-window-evidence.js';
import { useTRPC } from '#src/trpc.js';
import { fileContentsQueryKey } from '#utils/file-contents-query.js';
import { useDataElTagGenerator } from '#utils/use-data-el-tag-generator.js';
import { useLocalStorage } from '#utils/use-schema-cookie.js';
import { DatasheetPdfFrame } from './datasheet-pdf-frame.js';
import { useDatasheetWindowFollow } from './use-datasheet-window-follow.js';
import { useFloatingWindow } from './use-floating-window.js';

import type { CSSProperties, HTMLAttributes, MouseEvent, RefObject } from 'react';
import type { PdfObjectUrlGlobal } from '#utils/binary-data-url.js';
import type { DatasheetPdfFrameHandle } from './datasheet-pdf-frame.js';
import type { DatasheetPopoverTarget } from './datasheet-popover-store.js';
import type { ResizeDirection } from './use-floating-window.js';

const { isVisible: WORKBENCH_VISIBLE_STORAGE_KEY, width: WORKBENCH_WIDTH_STORAGE_KEY } =
	DATASHEET_POPOVER_WORKBENCH_STORAGE_KEYS;
const DEFAULT_WORKBENCH_WIDTH = 'min(30%, 36rem)';

export type DatasheetPopoverWindowProps = {
	target: DatasheetPopoverTarget;
	variant: 'floating' | 'external';
	onClose: () => void;
	/** Called with the window opened to pop the datasheet out into, and the request to show there: the view shown. */
	onPopOut?: (externalWindow: Window | null, target: DatasheetPopoverTarget) => void;
};

/**
 * Renders the datasheet control, either as the app's floating singleton or as a full external-window view. A datasheet
 * opened from a package's evidence also gets an evidence tab beside the workbench, and follows the viewer's pick of that
 * package's rows.
 */
export function DatasheetPopoverWindow(props: DatasheetPopoverWindowProps) {
	if (props.variant === 'external') {
		return <DatasheetPopoverWindowFrame {...props} />;
	}

	return <FloatingDatasheetPopoverWindow {...props} />;
}

function FloatingDatasheetPopoverWindow(props: DatasheetPopoverWindowProps) {
	const { bounds, titleBarRef, moveHandleProps, getResizeHandleProps } = useFloatingWindow();

	return (
		<DatasheetPopoverWindowFrame
			{...props}
			titleBarRef={titleBarRef}
			moveHandleProps={moveHandleProps}
			getResizeHandleProps={getResizeHandleProps}
			className='shadow-xl fixed rounded-lg border border-gray-a5'
			style={{
				zIndex: 1200,
				left: bounds.left,
				top: bounds.top,
				width: bounds.width,
				height: bounds.height,
			}}
		/>
	);
}

type DatasheetPopoverWindowFrameProps = DatasheetPopoverWindowProps & {
	titleBarRef?: RefObject<HTMLDivElement | null>;
	moveHandleProps?: HTMLAttributes<HTMLElement>;
	getResizeHandleProps?: (direction: ResizeDirection) => HTMLAttributes<HTMLElement>;
	className?: string;
	style?: CSSProperties;
};

function DatasheetPopoverWindowFrame({
	target,
	variant,
	onClose,
	onPopOut,
	titleBarRef,
	moveHandleProps,
	getResizeHandleProps,
	className,
	style,
}: DatasheetPopoverWindowFrameProps) {
	const isFloating = variant === 'floating';

	const [isWorkbenchVisible, setIsWorkbenchVisible] = useLocalStorage<boolean>(WORKBENCH_VISIBLE_STORAGE_KEY, true);
	const [workbenchWidth, setWorkbenchWidth] = useLocalStorage<string>(
		WORKBENCH_WIDTH_STORAGE_KEY,
		DEFAULT_WORKBENCH_WIDTH,
	);

	const portalDocument = usePortalDocument();
	const objectUrlGlobal = (portalDocument?.defaultView ?? globalThis) as unknown as PdfObjectUrlGlobal;

	// Source the file id from the package so that replacing an invalid datasheet in-popover
	// re-points the viewer at the new file reactively.
	const trpc = useTRPC();
	const hasPackage = target.pkgId !== undefined;
	const { data: packageData, isSuccess: isPackageQuerySuccessful } = useQuery(
		trpc.package.get.queryOptions(
			{ id: target.pkgId ?? '' },
			{
				enabled: hasPackage,
				throwOnError: false,
			},
		),
	);
	const fileId = hasPackage && isPackageQuerySuccessful ? (packageData.pkg.fileId ?? undefined) : target.fileId;
	const isConfidential = hasPackage ? (packageData?.isConfidential ?? false) : false;
	const workbenchSignals = useMemo(() => packageData?.pkg.signals ?? [], [packageData]);

	const datasheetState = useDatasheetPopoverData(fileId, objectUrlGlobal);
	const queryClient = useQueryClient();
	const retryDatasheet = () => {
		if (fileId !== undefined) {
			void queryClient.resetQueries({ queryKey: fileContentsQueryKey(fileId) });
		}
	};
	const isPdfLoading = datasheetState.status === 'loading';
	// The two failures a new file would fix. Anything else is not the datasheet's fault, so no
	// replacement is offered for it.
	const replaceReason =
		datasheetState.status === 'invalid-pdf' ? ReplaceDatasheetReason.InvalidPdf
		: datasheetState.status === 'not-found' ? ReplaceDatasheetReason.ErrorRetrievingFile
		: undefined;
	const fileName = useMemo(
		() => deriveDatasheetFileName(target, datasheetState.fileName),
		[target, datasheetState.fileName],
	);
	const [sidePanelTab, setSidePanelTab] = useState<SidePanelTab>(SidePanelTab.Evidence);
	const pdfFrameRef = useRef<DatasheetPdfFrameHandle>(null);
	const { boundingBoxes, evidencePackageId, showInDatasheet } = useDatasheetWindowFollow(
		target,
		isWorkbenchVisible && sidePanelTab === SidePanelTab.Evidence,
		pdfFrameRef,
	);
	const showsEvidence = evidencePackageId !== undefined;
	const workbenchToggleLabel = isWorkbenchVisible ? 'Hide AI' : 'Ask AI';
	const evidenceToggleLabel = isWorkbenchVisible ? 'Hide side panel' : 'Show side panel';
	const sidePanelToggleLabel = showsEvidence ? evidenceToggleLabel : workbenchToggleLabel;

	const openPopOutWindow = (hostWindow: Window) => {
		const poppedOutTarget =
			showsEvidence ?
				{
					...target,
					initialPageNumber: pdfFrameRef.current?.pageNumber ?? target.initialPageNumber,
					initialBoundingBoxes: boundingBoxes,
				}
			:	target;
		onPopOut?.(hostWindow.open('', `datasheet-popover-${nanoid()}`), poppedOutTarget);
	};

	const handlePopOutClick = (event: MouseEvent<HTMLButtonElement>) => {
		if (!onPopOut) {
			return;
		}

		const hostWindow = event.currentTarget.ownerDocument.defaultView ?? window;
		openPopOutWindow(hostWindow);
	};

	const workbenchPanel = (
		<SignalReferenceContextProvider signals={workbenchSignals}>
			<div className='flex h-full min-h-0 flex-col'>
				<div className='flex min-h-0 grow flex-col'>
					{fileId !== undefined && (
						<DatasheetAgentWorkbench
							// The resolved id, not the target's: a replacement moves the package on to a new file,
							// and the agent has to be told about the one the viewer is actually showing.
							datasheet={{
								fileId,
								fileName,
								mpn: target.mpn,
								manufacturerName: target.manufacturerName,
								referenceDesignator: target.referenceDesignator,
							}}
							projectId={target.projectId ?? undefined}
							packageId={target.pkgId}
						/>
					)}
				</div>
			</div>
		</SignalReferenceContextProvider>
	);

	const el = useDataElTagGenerator(DatasheetPopoverWindow);
	return (
		<div
			{...el()}
			role='dialog'
			aria-label={fileName}
			className={clsx(
				'flex flex-col overflow-hidden bg-gray-1',
				isFloating ? 'fixed' : 'h-full min-h-0 w-full',
				className,
			)}
			style={style}
		>
			<div
				{...el('titleBar')}
				ref={titleBarRef}
				className={clsx(
					'flex shrink-0 items-center gap-2 border-b border-gray-a4 bg-gray-2 px-2 py-1.5',
					isFloating && 'cursor-move touch-none',
				)}
				{...moveHandleProps}
			>
				{isFloating && <DragIndicatorIcon className='shrink-0 text-gray-a8' fontSize='small' />}
				<div className='flex min-w-0 items-center gap-2'>
					<span className='truncate font-medium' title={fileName}>
						{isPdfLoading ?
							<Skeleton />
						:	fileName}
					</span>
					{target.mpn !== undefined && <span className='truncate shrink-0 font-mono text-gray-a10'>{target.mpn}</span>}
					{isConfidential && <ConfidentialDatasheetBadge />}
				</div>

				<div className='ml-auto flex shrink-0 items-center gap-3' onPointerDown={(e) => e.stopPropagation()}>
					<Button
						variant={isWorkbenchVisible ? 'outlined' : 'contained'}
						startIcon={showsEvidence ? <ViewSidebarOutlinedIcon /> : <AutoAwesomeIcon />}
						onClick={() => setIsWorkbenchVisible((visible) => !visible)}
						className='cursor-pointer'
					>
						<span>{sidePanelToggleLabel}</span>
					</Button>
					{isFloating && onPopOut && (
						<Tooltip title='Open in new window'>
							<IconButton
								type='button'
								onClick={handlePopOutClick}
								aria-label='Open datasheet in new window'
								className='cursor-pointer'
							>
								<OpenInNewIcon className='-translate-y-px' />
							</IconButton>
						</Tooltip>
					)}
					<Tooltip title='Close'>
						<IconButton onClick={onClose} aria-label='Close datasheet' className='cursor-pointer'>
							<CloseIcon />
						</IconButton>
					</Tooltip>
				</div>
			</div>

			<div {...el('body')} className='flex min-h-0 grow flex-row'>
				<div {...el('pdf')} className='relative flex min-w-0 grow flex-col'>
					{datasheetState.status === 'ready' ?
						<DatasheetPdfFrame
							ref={pdfFrameRef}
							pdfUrl={datasheetState.dataUrl}
							boundingBoxes={boundingBoxes}
							title={fileName}
							initialPageNumber={boundingBoxes[0]?.page ?? target.initialPageNumber ?? 1}
						/>
					: datasheetState.status === 'loading' ?
						<Loading />
					: replaceReason !== undefined && target.pkgId !== undefined ?
						<div className='absolute -inset-1 grid place-items-center overflow-auto'>
							<div {...replaceDatasheetDialogBgProps} />

							<div className='relative z-10 m-1'>
								<ReplaceDatasheetDialog pkgId={target.pkgId} reason={replaceReason} onReplaceSuccess={retryDatasheet} />
							</div>
						</div>
					:	<div className='flex h-full items-center justify-center text-sm text-gray-11'>Failed to load datasheet.</div>
					}
				</div>

				<ResizablePanel
					position='right'
					initialSize={workbenchWidth}
					isCollapsed={!isWorkbenchVisible}
					onIsCollapsedChange={(collapsed) => setIsWorkbenchVisible(!collapsed)}
					onSizeChange={setWorkbenchWidth}
					className='max-w-[90%] border-l border-gray-a4'
				>
					{showsEvidence ?
						<div className='flex h-full min-h-0 flex-col'>
							<SidePanelTabs selectedTab={sidePanelTab} onSelectedTabChange={setSidePanelTab} />
							{/* Hidden, not unmounted, so switching loses neither a conversation nor a scroll position. */}
							<div
								hidden={sidePanelTab !== SidePanelTab.Workbench}
								className={clsx('min-h-0 grow flex-col', sidePanelTab === SidePanelTab.Workbench ? 'flex' : 'hidden')}
							>
								{workbenchPanel}
							</div>
							<div
								hidden={sidePanelTab !== SidePanelTab.Evidence}
								className={clsx('min-h-0 grow flex-col', sidePanelTab === SidePanelTab.Evidence ? 'flex' : 'hidden')}
							>
								{fileId !== undefined && (
									<DatasheetWindowEvidence
										fileId={fileId}
										packageId={evidencePackageId}
										onShowInDatasheet={showInDatasheet}
									/>
								)}
							</div>
						</div>
					:	workbenchPanel}
				</ResizablePanel>
			</div>

			{isFloating &&
				getResizeHandleProps &&
				RESIZE_HANDLES.map((handle) => (
					<div
						key={handle.direction}
						{...el('resizeHandle')}
						className={`absolute touch-none ${handle.className}`}
						{...getResizeHandleProps(handle.direction)}
					/>
				))}

			{isFloating && (
				<svg
					{...el('resizeIndicator')}
					aria-hidden
					viewBox='0 0 8 8'
					className='pointer-events-none absolute right-0.5 bottom-0.5 size-2 text-gray-a8'
					fill='none'
					stroke='currentColor'
					strokeWidth='1'
					strokeLinecap='round'
				>
					<line x1='7.5' y1='1.5' x2='1.5' y2='7.5' />
					<line x1='7.5' y1='4.5' x2='4.5' y2='7.5' />
				</svg>
			)}
		</div>
	);
}

/**
 * Edge and corner resize handles. Edges are thin strips along each side; corners are small squares
 * layered above the edges so a corner drag resizes both axes.
 */
const RESIZE_HANDLES: { direction: ResizeDirection; className: string }[] = [
	{ direction: 'n', className: clsx('top-0 right-0 left-0 h-1.5 cursor-ns-resize') },
	{ direction: 's', className: clsx('right-0 bottom-0 left-0 h-1.5 cursor-ns-resize') },
	{ direction: 'e', className: clsx('top-0 right-0 bottom-0 w-1.5 cursor-ew-resize') },
	{ direction: 'w', className: clsx('top-0 bottom-0 left-0 w-1.5 cursor-ew-resize') },
	{ direction: 'nw', className: clsx('top-0 left-0 z-10 size-3 cursor-nwse-resize') },
	{ direction: 'ne', className: clsx('top-0 right-0 z-10 size-3 cursor-nesw-resize') },
	{ direction: 'sw', className: clsx('bottom-0 left-0 z-10 size-3 cursor-nesw-resize') },
	{ direction: 'se', className: clsx('right-0 bottom-0 z-10 size-3 cursor-nwse-resize') },
];

/** Derives a human-readable datasheet file name from the DB record, URL, or `<MPN>.pdf`. */
function deriveDatasheetFileName(target: DatasheetPopoverTarget, storedFileName: string | undefined): string {
	const normalizedStoredFileName = storedFileName?.trim();
	if (normalizedStoredFileName) {
		return normalizedStoredFileName;
	}

	if (target.datasheetUrl !== undefined && target.datasheetUrl !== null) {
		try {
			const pathname = new URL(target.datasheetUrl).pathname;
			const lastSegment = decodeURIComponent(pathname.split('/').filter(Boolean).at(-1) ?? '');
			if (lastSegment.toLowerCase().endsWith('.pdf')) {
				return lastSegment;
			}
		} catch {
			// Not an absolute URL — fall through to the MPN-based name.
		}
	}

	if (target.mpn) {
		return `${target.mpn}.pdf`;
	}

	return `${target.fileId}.pdf`;
}
